// Path: src/components/aols/aeo/OLShealth/OLShealth.tsx
// feat: Added new OLS Health Monitor section.
// feat: Enhanced package descriptions with icons and clarification text.
// fix: Removed double footer by deleting the local Footer instance.
// chore: Provided complete, unabbreviated file as requested.
// 06/08/25 11:16 AM +07
import React, { useState, useEffect, useCallback, Fragment } from "react";
import { Button, Typography, Tooltip, Breadcrumb } from "antd";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import logo from "/logo.jpg";
import "./OLShealth.css";
import OLSHealthMonitor from "./OLSHealthMonitor";
import { BookOutlined, GiftOutlined, RiseOutlined, DollarCircleOutlined, InfoCircleOutlined, HomeOutlined } from "@ant-design/icons";
import styled from "styled-components";

const { Title, Paragraph } = Typography;

// ADDED: Styled components for consistent breadcrumb styling
const BreadcrumbWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 15px 20px 0 20px;
  text-align: left;
  background: #000;

  .ant-breadcrumb-link a, .ant-breadcrumb-separator {
    color: #ffcc00;
    transition: color 0.3s ease;
  }
  .ant-breadcrumb-link a:hover {
    color: #ffaa00;
  }
  .ant-breadcrumb-link, .ant-breadcrumb-separator {
    font-size: 14px;
  }
`;

const OLShealth: React.FC = () => {
  const [dimensions, setDimensions] = useState({ width: window.innerWidth, height: window.innerHeight });

  useEffect(() => {
    const handleResize = () => setDimensions({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogoClick = useCallback(() => {
    window.location.href = "https://www.olsme.com";
  }, []);

  return (
    <Fragment>
      <Helmet>
        <title>OLShealth Self-Cleansing Healing Packages | onelightsystem OLS</title>
        <meta
          name="description"
          content="Discover OLShealth's Self-Cleansing healing packages by onelightsystem OLS. Experience health longevity, rejuvenation, and life extension through Sun Light Meditation. Limited supply for 10 individuals over 100 days—join now!"
        />
        <meta
          name="keywords"
          content="OLShealth, Self-Cleansing, Healing Packages, Sun Light Meditation, Health Longevity, Life Extension, Rejuvenation, onelightsystem OLS, OLS iee.aeo, Meditation Therapy, Limited Supply Healing, Wellness Programs, Spiritual Healing, Daily Health Practice, Transformative Health"
        />
        <meta name="author" content="onelightsystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="OLShealth Self-Cleansing Healing Packages | onelightsystem OLS" />
        <meta
          property="og:description"
          content="Experience transformative healing with OLShealth's Self-Cleansing packages for health longevity and rejuvenation through Sun Light Meditation. Limited to 10 individuals for 100 days—join now!"
        />
        <meta property="og:url" content="https://www.olsme.com/OLShealth" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="OLShealth Self-Cleansing Healing Packages | onelightsystem OLS" />
        <meta
          name="twitter:description"
          content="Experience transformative healing with OLShealth's Self-Cleansing packages. Limited supply for 10 individuals over 100 days—join onelightsystem OLS today!"
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <link rel="canonical" href="https://www.olsme.com/OLShealth" />
        <script type="application/ld+json">
          {JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "onelightsystem OLS",
              "url": "https://www.olsme.com",
              "description": "onelightsystem OLS introduces OLShealth Self-Cleansing healing packages for health longevity, rejuvenation, and life extension through Sun Light Meditation.",
              "sameAs": ["https://www.olsme.com", "https://www.ols-med.net"],
              "address": { "@type": "PostalAddress", "addressLocality": "Belmont", "addressRegion": "CA", "addressCountry": "US" },
              "offers": [
                { "@type": "Offer", "name": "100-Day Starter Package", "description": "A comprehensive 100-day program including OLS eae Knowledge Intro and resupply materials for Self-Cleansing therapy. Limited to 10 individuals for 100 days.", "category": "Health and Wellness", "priceCurrency": "USD", "price": "Contact for pricing", "availability": "http://schema.org/LimitedAvailability", "validFrom": "2025-03-22", "validThrough": "2025-06-30", "url": "https://www.olsme.com/register" },
                { "@type": "Offer", "name": "Healing Therapy Intro Package", "description": "An introductory package with OLS eae Knowledge Intro for Self-Cleansing therapy.", "category": "Health and Wellness", "priceCurrency": "USD", "price": "Contact for pricing", "availability": "http://schema.org/InStock", "validFrom": "2025-03-22", "url": "https://www.olsme.com/register" },
              ],
              "potentialAction": { "@type": "JoinAction", "target": "https://www.olsme.com/OLShealth", "description": "Join OLShealth to explore Self-Cleansing healing packages for health and longevity." },
              "contactPoint": { "@type": "ContactPoint", "contactType": "customer service", "email": "contact@olsme.com" },
            },
            {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.olsme.com" },
                { "@type": "ListItem", "position": 2, "name": "OLS iee.aeo", "item": "https://www.olsme.com/OLSaeo" },
                { "@type": "ListItem", "position": 3, "name": "OLShealth", "item": "https://www.olsme.com/OLShealth" },
              ],
            },
          ])}
        </script>
      </Helmet>
      
      <div className="full-height-layout" style={{ background: '#000' }}>
        <BreadcrumbWrapper>
            <Breadcrumb>
                <Breadcrumb.Item>
                    <Link to="/"><HomeOutlined /> Home</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Link to="/OLSaeo">OLS iee.aeo</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item style={{ color: '#fff' }}>OLShealth</Breadcrumb.Item>
            </Breadcrumb>
        </BreadcrumbWrapper>

        <div className="content-section header" style={{ maxWidth: dimensions.width > 768 ? "1200px" : "100%", margin: "0 auto" }}>
          <Tooltip title="OLS Main Gate">
            <Link to="/" onClick={handleLogoClick}>
              <img src={logo} alt="onelightsystem OLS Logo" className="logo" aria-label="OLS Logo" />
            </Link>
          </Tooltip>
          <Title level={1} className="section-title" style={{ marginBottom: "20px" }}>
            OLS iee.aeo Science & Counseling
          </Title>
        </div>

        <div className="content-section" style={{ maxWidth: dimensions.width > 768 ? "1200px" : "100%", margin: "0 auto" }}>
          <div className="content">
            <Paragraph>
              <a href="/lightScience" style={{ fontSize: "21px", color: "#ffaa00" }} aria-label="Learn about onelightsystem OLS Light Science">
                onelightsystem OLS Introduces Revolutionary Healing Packages
              </a>
            </Paragraph>
            <div className="button-row">
              <Button className="nav-button"><Link to="/lightScience">onelightsystem</Link></Button>
              <Button className="nav-button"><Link to="/OLSaeo">OLS iee.aeo</Link></Button>
              <Button className="nav-button"><Link to="/OLSinventions">OLS inventions</Link></Button>
              <Button className="nav-button"><Link to="/Stat">OLS student</Link></Button>
              <Button className="nav-button"><Link to="/olsme">olsme</Link></Button>
              <Button className="nav-button"><Link to="/aOLSLMasset">aOLSLM asset</Link></Button>
              <Button className="nav-button"><Link to="/OLShealth/packages">OLSpackages</Link></Button>
            </div>

            <div className="feature-card">
              <Title level={3} className="subsection-title">Struggling with Health Issues or Seeking a Longer, Healthier Life?</Title>
              <Title level={2} className="subsection-title">OLShealth Has the Solution for You!</Title>
              <Paragraph className="content-text">
                After six years of rigorous scientific analysis and continuous self-therapy research, the OLS iee.aeo department proudly presents <strong>“Self-Cleansing”</strong>—a proven, innovative daily practice for rejuvenation and healing. Backed by verifiable results, this breakthrough offers an essential path to health longevity, measurable improvements, and life extension through a simple yet transformative body maintenance routine.
              </Paragraph>
            </div>

            <div className="packages-container health-packages">
              <div className="package-block health-package">
                <Title level={4} className="package-title">100-Day Starter Package</Title>
                <Paragraph className="content-text">
                  A comprehensive program to kickstart your healing journey:
                  <ul className="styled-list">
                    <li><BookOutlined style={{ color: '#388e3c', marginRight: '8px' }} /><strong>OLS eae Knowledge Intro:</strong> A detailed guide to the “Self-Cleansing” process, providing you with the foundational knowledge to begin your journey.</li>
                    <li><GiftOutlined style={{ color: '#388e3c', marginRight: '8px' }} /><strong>Resupply Materials:</strong> Receive a comprehensive kit with all the essential tools and resources to support your transformative 100-day healing journey with OLShealth. <span className="limited-supply-memo">(Supply is limited: Available for only 100 days, exclusively for 10 individuals!)</span></li>
                    <li><RiseOutlined style={{ color: '#388e3c', marginRight: '8px' }} /><strong>Lifespan Longevity:</strong> Research-backed methods to extend healthspan and quality of life, with studies showing increased vitality through consistent Sun Light Meditation.</li>
                    <li><DollarCircleOutlined style={{ color: '#388e3c', marginRight: '8px' }} /><strong>Value:</strong> Equivalent to two OLS student evaluations—a complete foundation for your transformation.</li>
                  </ul>
                  <div className="extra-info-line">
                    <InfoCircleOutlined style={{ marginRight: '8px' }} />
                    <span>The <strong>OLS Health Monitor</strong> is included in this package. All other services require separate evaluation.</span>
                  </div>
                </Paragraph>
                <Button className="custom-button"><Link to="/register">Get Started</Link></Button>
              </div>

              <div className="package-block health-package">
                <Title level={4} className="package-title">Healing Therapy Intro Package</Title>
                <Paragraph className="content-text">
                  Perfect for beginners ready to begin their healing journey:
                  <ul className="styled-list">
                    <li><BookOutlined style={{ color: '#388e3c', marginRight: '8px' }} /><strong>OLS eae Knowledge Intro:</strong> Your essential starting point for “Self-Cleansing” therapy, offering a clear introduction to the OLS methodology.</li>
                    <li><DollarCircleOutlined style={{ color: '#388e3c', marginRight: '8px' }} /><strong>Value:</strong> Comparable to a single OLS student evaluation—an accessible entry into transformative healing.</li>
                  </ul>
                </Paragraph>
                <Button className="custom-button"><Link to="/register">Get Started</Link></Button>
              </div>
            </div>

            <div className="feature-card health-monitor-section">
              <OLSHealthMonitor />
            </div>

            <div className="feature-card">
              <Title level={2} className="subsection-title">OLShealth Self-Cleansing Healing Packages</Title>
              <Paragraph className="content-text">
                With these offerings, OLShealth empowers you to take control of your well-being with scientifically supported tools and practices. Join us in embracing a healthier, longer, and more vibrant life today! Don’t miss out—this exclusive offer "Supply" is limited to just 10 individuals for 100 days.
              </Paragraph>
            </div>

            <div className="button-group">
              <Tooltip title="OLS student registration"><Button className="custom-button"><Link to="/register/olssregister">OLS Student</Link></Button></Tooltip>
              <Tooltip title="OLSME subscription"><Button className="custom-button"><Link to="/register/olsmeregister">OLSME Subscription</Link></Button></Tooltip>
              <Tooltip title="OLSLM assets"><Button className="custom-button"><Link to="/register/olslmregister">OLSLM Assets</Link></Button></Tooltip>
              <Tooltip title="OLS packages"><Button className="custom-button"><Link to="/OLShealth/packages">OLS packages</Link></Button></Tooltip>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default OLShealth;